import { GovernanceOverlay } from '../types/governanceOverlays';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

class GovernanceOverlaysService {
  private async makeRequest(
    endpoint: string,
    options: RequestInit = {},
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Token expired or invalid, clear user data
      setUserAndToken(null, '');
      throw new Error('Authentication failed. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getGovernanceOverlays(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<GovernanceOverlay[]> {
    const response = await this.makeRequest(
      '/governance-overlays',
      { method: 'GET' },
      accessToken,
      setUserAndToken
    );
    return response.data;
  }

  async getGovernanceOverlay(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<GovernanceOverlay> {
    const response = await this.makeRequest(
      `/governance-overlays/${id}`,
      { method: 'GET' },
      accessToken,
      setUserAndToken
    );
    return response;
  }

  async createGovernanceOverlay(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    data: any
  ): Promise<GovernanceOverlay> {
    const response = await this.makeRequest(
      '/governance-overlays',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      accessToken,
      setUserAndToken
    );
    return response.data;
  }

  async updateGovernanceOverlay(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    data: any
  ): Promise<GovernanceOverlay> {
    const response = await this.makeRequest(
      `/governance-overlays/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      accessToken,
      setUserAndToken
    );
    return response.data;
  }

  async upsertDescriptions(
    id: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    descriptions: any[]
  ): Promise<GovernanceOverlay> {
    const response = await this.makeRequest(
      `/governance-overlays/${id}/descriptions`,
      {
        method: 'POST',
        body: JSON.stringify({ entries: descriptions }),
      },
      accessToken,
      setUserAndToken
    );
    return response.data;
  }

  async deleteDescription(
    id: string,
    key: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<GovernanceOverlay> {
    const response = await this.makeRequest(
      `/governance-overlays/${id}/descriptions/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
      accessToken,
      setUserAndToken
    );
    return response.data;
  }
}

export const governanceOverlaysService = new GovernanceOverlaysService();
